import { useQuery } from "@tanstack/react-query";
import {
  getSupportBannerStatus,
  SupportBannerStatusResponse,
  SupportServiceStatus,
} from "@/services/apis/serviceStatusApi";

export const SERVICE_BANNER_STATUS_QUERY_KEY = ["support", "banner", "status"];

const isOperationalStatus = (status?: string): boolean => {
  return (status || "").trim().toLowerCase() === "operational";
};

type UseServiceStatusOptions = {
  enabled?: boolean;
};

export const useServiceStatus = ({ enabled = true }: UseServiceStatusOptions = {}) => {
  const query = useQuery({
    queryKey: SERVICE_BANNER_STATUS_QUERY_KEY,
    queryFn: getSupportBannerStatus,
    enabled,
    select: (data: SupportBannerStatusResponse) => {
      const nonOperationalServices = (data.services || []).filter(
        (service: SupportServiceStatus) => !isOperationalStatus(service.effectiveStatus)
      );

      const hasServiceDisruption = data.globalStatus
        ? !isOperationalStatus(data.globalStatus)
        : nonOperationalServices.length > 0;

      return {
        ...data,
        nonOperationalServices,
        hasServiceDisruption,
      };
    },
    // Fail-open by design: if this endpoint errors, app remains accessible.
    // staleTime matches refetchInterval (1 min) so the interval is the sole refresh
    // trigger — window focus / mount do not fire extra requests on top of it.
    staleTime: 1 * 60 * 1000,
    retry: false,
    refetchInterval: enabled ? 60_000 : false,
    refetchOnWindowFocus: true,
  });

  return {
    ...query,
    nonOperationalServices: query.data?.nonOperationalServices || [],
    hasServiceDisruption: query.data?.hasServiceDisruption || false,
  };
};
